import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Badge } from "../../../../components/ui/badge";
import {
  AlertTriangle,
  Monitor,
  ArrowLeft,
  CheckCircle,
  Plus,
  X,
  Edit,
  Save,
  KeyRound,
  Loader2
} from "lucide-react";
import { useToast } from "../../../../hooks/use-toast";
import { SettingProps } from "../interface";
import { azureConfig, azureFetchConfig, azureUnConfig, azureUpdateClientSecret, azureUpdateSubIds } from "../Api/settingsApi";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { VscAzure } from "react-icons/vsc";
import useCloudSyncStore from "../../../../services/useCloudSyncStore";
import { refreshCloudCredentialStatuses, useCloudCredentialExpiry } from "../../../../services/useCloudCredentialStore";
import { CloudCredentialExpiredIcon } from "../../CloudCredentialExpired";
import { getCloudAccountSelection } from "../../cloudAccountSelection";

interface AzureSetupData {
  clientId: string;
  tenantId: string;
  clientSecret: string;
  subscriptionIds: string[];
  isConfigured: boolean;
}

const MASKED_VALUE = "**********";

export default function AzureSettings({ onBack }: SettingProps) {
  const [azureData, setAzureData] = useState<AzureSetupData>({
    clientId: "",
    tenantId: "",
    clientSecret: "",
    subscriptionIds: [],
    isConfigured: false
  });

  const [subscriptionInput, setSubscriptionInput] = useState("");
  const [editingSubscriptions, setEditingSubscriptions] = useState(false);
  const [originalSubscriptionIds, setOriginalSubscriptionIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingClientSecret, setEditingClientSecret] = useState(false);
  const [newClientSecret, setNewClientSecret] = useState("");
  const [isUpdatingClientSecret, setIsUpdatingClientSecret] = useState(false);
  const { toast } = useToast();
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const userData = useUserStore((state) => state.user);
  const credentialExpiry = useCloudCredentialExpiry("azure", tenantName);
  const credentialsExpired = azureData.isConfigured && credentialExpiry.expired;

  const handleFieldChange = (field: keyof AzureSetupData, value: string) => {
    setAzureData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitConfiguration = async () => {
    if (!azureData.clientId || !azureData.tenantId || !azureData.clientSecret) {
      toast({
        title: "Missing Information",
        description: "Please provide Client ID, Tenant ID, and Client Secret.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const subscriptions = subscriptionInput
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      let payload = {
        clientId: azureData.clientId,
        tenantId: azureData.tenantId,
        clientSecret: azureData.clientSecret,
        wsTenantName: tenantName,
        ws_tenant_email: userData?.email,
        subscriptionIds: subscriptions.length > 0 ? subscriptions : [],
      }
      await azureConfig(tenantName, payload);

      setAzureData(prev => ({
        ...prev,
        clientId: MASKED_VALUE,
        tenantId: MASKED_VALUE,
        clientSecret: MASKED_VALUE,
        subscriptionIds: subscriptions,
        isConfigured: true
      }));

      toast({
        title: "Azure Configuration Complete",
        description: "Your Azure integration has been successfully configured.",
      });
    } catch (error: any) {
      toast({
        title: "Configuration Failed",
        description: (typeof error === "string" ? error : error?.message) || "Failed to configure Azure settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubscription = () => {
    const newId = subscriptionInput.trim();
    if (newId && azureData.subscriptionIds.includes(newId)) {
      toast({
        title: "Duplicate Subscription ID",
        description: "This subscription ID has already been added.",
        variant: "destructive"
      });
      return;
    }
    if (newId) {
      setAzureData(prev => ({
        ...prev,
        subscriptionIds: [...prev.subscriptionIds, newId]
      }));
      setSubscriptionInput("");
      toast({
        title: "Subscription Added",
        description: `Subscription ID ${newId} has been added.`,
      });
    }
  };

  const handleRemoveSubscription = (subscriptionId: string) => {
    setAzureData(prev => ({
      ...prev,
      subscriptionIds: prev.subscriptionIds.filter(id => id !== subscriptionId)
    }));
    toast({
      title: "Subscription Removed",
      description: `Subscription ID ${subscriptionId} has been removed.`,
    });
  };

  const handleToggleClientSecretEditing = () => {
    setNewClientSecret("");
    setEditingClientSecret(prev => !prev);
  };

  const handleUpdateClientSecret = async () => {
    if (!newClientSecret.trim()) return;
    setIsUpdatingClientSecret(true);
    try {
      await azureUpdateClientSecret(tenantName, newClientSecret.trim());
      setNewClientSecret("");
      setEditingClientSecret(false);
      refreshCloudCredentialStatuses(tenantName);
      toast({
        title: "Client Secret Updated",
        description: "The Azure client secret has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Update Failed",
        description: (typeof error === "string" ? error : error?.message) || "Failed to update the client secret. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingClientSecret(false);
    }
  };

  const handleStartEditingSubscriptions = () => {
    setOriginalSubscriptionIds([...azureData.subscriptionIds]);
    setEditingSubscriptions(true);
  };

  const handleCancelEditingSubscriptions = () => {
    setAzureData(prev => ({
      ...prev,
      subscriptionIds: [...originalSubscriptionIds]
    }));
    setSubscriptionInput("");
    setEditingSubscriptions(false);
  };

  const handleSaveSubscriptions = async () => {
    setIsLoading(true);
    try {
      await azureUpdateSubIds(azureData.subscriptionIds, tenantName)
      const currentSubscriptionId = getCloudAccountSelection("azure").id;
      const selectedSubscriptionId = azureData.subscriptionIds.includes(currentSubscriptionId)
        ? currentSubscriptionId
        : azureData.subscriptionIds[0] || "";
      myLocalStorage.setItem("latestSubList", JSON.stringify({
        azureSubscriptionId: selectedSubscriptionId,
        subscriptionName: selectedSubscriptionId || "None",
      }));
      useCloudSyncStore.getState().startSyncPolling("azure", tenantName);
      toast({
        title: "Subscriptions Updated",
        description: "Subscription IDs have been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update subscriptions:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update subscription IDs. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setEditingSubscriptions(false);
    }
  };

  const handleUnconfigure = async () => {
    setIsLoading(true);
    try {
      await azureUnConfig(tenantName);
      refreshCloudCredentialStatuses(tenantName);
      setAzureData({
        clientId: "",
        tenantId: "",
        clientSecret: "",
        subscriptionIds: [],
        isConfigured: false
      });
      setSubscriptionInput("");
      setEditingSubscriptions(false);
      setEditingClientSecret(false);
      setNewClientSecret("");
      toast({
        title: "Azure Configuration Removed",
        description: "Your Azure integration has been unconfigured.",
      });
    } catch (error: any) {
      toast({
        title: "Unconfiguration Failed",
        description: (typeof error === "string" ? error : error?.message) || "Failed to unconfigure Azure. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAzureConfig = async () => {
    try {
      const response = await azureFetchConfig(tenantName);

      if (response) {
        const { subscriptionIds } = response;

        setAzureData({
          clientId: MASKED_VALUE,
          tenantId: MASKED_VALUE,
          clientSecret: MASKED_VALUE,
          subscriptionIds: subscriptionIds || [],
          isConfigured: true,
        });
      }
    } catch (e) {
      console.error("Failed to fetch Azure config:", e);
    }
  };

  useEffect(() => {
    fetchAzureConfig();
  }, [])

  const canSubmit = azureData.clientId && azureData.tenantId && azureData.clientSecret;

  return (
    <div className="tenant-settings-page flex-1 space-y-6 p-6">
      {/* Header */}
      <Card className="settings-header-card border-border/40 rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="settings-back-button"
              onClick={onBack}
              aria-label="Back to settings"
              title="Back to settings"
            >

              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
              <VscAzure className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" data-testid="text-page-title">
                Azure Setup
                {credentialsExpired && <CloudCredentialExpiredIcon reason={credentialExpiry.reason} />}
              </h1>
              <p className="text-muted-foreground">
                Configure Microsoft Azure integration
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Azure Setup Content */}
      <div className="space-y-6">
        {/* Azure Initial Setup */}
        <Card className="border-border/40 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Azure Initial Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-id">Client Id</Label>
              <Input
                id="client-id"
                value={azureData.clientId}
                onChange={(e) => handleFieldChange("clientId", e.target.value)}
                placeholder="Enter Azure Application (client) ID"
                data-testid="input-client-id"
                disabled={azureData.isConfigured || isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-id">Tenant Id</Label>
              <Input
                id="tenant-id"
                value={azureData.tenantId}
                onChange={(e) => handleFieldChange("tenantId", e.target.value)}
                placeholder="Enter Azure Directory (tenant) ID"
                data-testid="input-tenant-id"
                disabled={azureData.isConfigured || isLoading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="client-secret">Client Secret</Label>
                {azureData.isConfigured && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleClientSecretEditing}
                    disabled={isUpdatingClientSecret || isLoading}
                    className={`gap-2 ${credentialsExpired && !editingClientSecret
                      ? "border-red-300 text-red-600 dark:border-red-800 dark:text-red-400"
                      : ""}`}
                    data-testid="button-update-client-secret"
                  >
                    {editingClientSecret ? (
                      <>
                        <X className="w-4 h-4" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <KeyRound className="w-4 h-4" />
                        Update
                      </>
                    )}
                  </Button>
                )}
              </div>
              {editingClientSecret ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="client-secret"
                      type="password"
                      autoComplete="new-password"
                      value={newClientSecret}
                      onChange={(e) => setNewClientSecret(e.target.value)}
                      placeholder="Enter the new Azure client secret value"
                      disabled={isUpdatingClientSecret}
                      className="flex-1"
                      data-testid="input-new-client-secret"
                    />
                    <Button
                      onClick={handleUpdateClientSecret}
                      disabled={!newClientSecret.trim() || isUpdatingClientSecret}
                      className="gap-2"
                      data-testid="button-save-client-secret"
                    >
                      {isUpdatingClientSecret ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Secret
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Use the secret Value from Certificates &amp; secrets in the app registration, not the Secret ID.
                  </p>
                </div>
              ) : (
                <Input
                  id="client-secret"
                  type={azureData.isConfigured ? "text" : "password"}
                  value={azureData.clientSecret}
                  onChange={(e) => handleFieldChange("clientSecret", e.target.value)}
                  placeholder="Enter Azure Client Secret"
                  data-testid="input-client-secret"
                  disabled={azureData.isConfigured || isLoading}
                />
              )}
            </div>

            {!azureData.isConfigured ? (
              <div className="space-y-2">
                <Label htmlFor="subscription-ids">Subscription IDs (Optional)</Label>
                <Textarea
                  id="subscription-ids"
                  value={subscriptionInput}
                  onChange={(e) => setSubscriptionInput(e.target.value)}
                  placeholder="Enter Subscription IDs separated by commas"
                  rows={3}
                  disabled={isLoading}
                  data-testid="textarea-subscription-ids"
                />
                <p className="text-sm text-muted-foreground">
                  Separate multiple Subscription IDs using commas.
                </p>
              </div>
            ) : (
              /* Subscription Management for Configured State */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Subscription IDs (Optional)</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={editingSubscriptions ? handleCancelEditingSubscriptions : handleStartEditingSubscriptions}
                    disabled={isLoading}
                    className="gap-2"
                    data-testid="button-edit-subscriptions"
                  >
                    {editingSubscriptions ? (
                      <>
                        <X className="w-4 h-4" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <Edit className="w-4 h-4" />
                        Edit
                      </>
                    )}
                  </Button>
                </div>

                {editingSubscriptions && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={subscriptionInput}
                        onChange={(e) => setSubscriptionInput(e.target.value)}
                        placeholder="Add new subscription ID"
                        disabled={isLoading}
                        className="flex-1"
                        data-testid="input-new-subscription"
                      />
                      <Button
                        onClick={handleAddSubscription}
                        disabled={!subscriptionInput.trim() || isLoading}
                        size="sm"
                        className="gap-2"
                        data-testid="button-add-subscription"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {azureData.subscriptionIds.length > 0 ? (
                    <div className="grid gap-2">
                      {azureData.subscriptionIds.map((subscriptionId, index) => (
                        <div
                          key={index}
                          className="settings-muted-card flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                        >
                          <code className="text-sm font-mono">{subscriptionId}</code>
                          {editingSubscriptions && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveSubscription(subscriptionId)}
                              disabled={isLoading}
                              className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                              data-testid={`button-remove-subscription-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p className="text-sm">No subscription IDs configured</p>
                      {editingSubscriptions && (
                        <p className="text-xs">Add subscription IDs above to manage Azure resources</p>
                      )}
                    </div>
                  )}
                </div>

                {editingSubscriptions && (
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveSubscriptions}
                      disabled={isLoading}
                      className="gap-2"
                      data-testid="button-save-subscriptions"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {!azureData.isConfigured && (
              <div className="pt-4">
                <Button
                  onClick={handleSubmitConfiguration}
                  disabled={!canSubmit || isLoading}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="button-submit-configuration"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Configuring...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit Configuration
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration Status */}
        {azureData.isConfigured && (
          <Card className={`rounded-2xl ${credentialsExpired
            ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950"
            : "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950"}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {credentialsExpired ? (
                    <>
                      <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0" />
                      <div data-testid="banner-credentials-expired">
                        <h3 className="font-semibold text-red-900 dark:text-red-100">
                          Azure Credentials Expired
                        </h3>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {credentialExpiry.reason ? `${credentialExpiry.reason}. ` : ""}Log collection and sync are paused.
                          Update the client secret above with a new valid secret to resume.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      <div>
                        <h3 className="font-semibold text-green-900 dark:text-green-100">
                          Azure Configuration Complete
                        </h3>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Your Azure integration is successfully configured. You can manage subscription IDs above.
                        </p>
                      </div>
                    </>
                  )}
                  <Badge variant="outline" className={`ml-4 ${credentialsExpired
                    ? "bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700"
                    : "bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700"}`}>
                    {azureData.subscriptionIds.length} Subscription{azureData.subscriptionIds.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <Button
                  onClick={handleUnconfigure}
                  variant="destructive"
                  size="sm"
                  disabled={isLoading}
                  className="gap-2"
                  data-testid="button-unconfigure-azure"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Unconfiguring...
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4" />
                      Unconfigure Azure Account
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
